<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

import {
  TuiAccordionContent,
  TuiAccordionHeader,
  TuiAccordionPanel,
} from "@vm/tui";

import Accordion from "primevue/accordion";

import CategoryPanel from "./category-panel.vue";
import ComparisonHeader from "./comparison-header.vue";
import ComparisonTable from "./comparison-table.vue";

import { useComparisonConfigStore } from "@/config/config-store";

import { getCategoryForObject, getCharacteristicGroups } from "../comparison-utils";

import type { ComparisonObject } from "../comparison-data-service";
import type { StoredObject } from "../comparison-store";

const props = defineProps<{
  objects: StoredObject[];
}>();

const emit = defineEmits<{
  move: [nameA: string, nameB: string];
  remove: [name: string];
}>();

const hideIdentical = ref(false);
const expandedPanels = ref<string[]>([]);

function onMove(fromIndex: number, toIndex: number) {
  emit("move", props.objects[fromIndex].key, props.objects[toIndex].key);
}

const configStore = useComparisonConfigStore();

const { colors, minColumnWidth } = storeToRefs(configStore);

const columnLayout = computed(() => {
  const w = minColumnWidth.value;
  const count = props.objects.length;
  return {
    gridTemplateColumns: `${w}px repeat(${count}, minmax(${w}px, 1fr))`,
    minWidth: `${(count + 1) * w}px`,
    stickyWidth: w,
  };
});

const objectColumnsStyle = computed(() => {
  const w = minColumnWidth.value;
  const count = props.objects.length;
  return {
    gridTemplateColumns: `repeat(${count}, minmax(${w}px, 1fr))`,
    minWidth: `${count * w}px`,
  };
});

const typeBoundaryIndex = computed(() => {
  return props.objects.findIndex(
    (obj, i) => i > 0 && obj.type?.value !== props.objects[i - 1]?.type?.value,
  );
});

const subjectObject = computed(() => props.objects[0]);

const categoryNames = computed(() => {
  const names = new Set<string>();
  for (const obj of props.objects) {
    for (const cat of getCharacteristicGroups(obj.characteristics?.value)) {
      names.add(cat.Formatted_name);
    }
  }
  return [...names];
});

function getCharacteristicValue(
  obj: ComparisonObject,
  categoryName: string,
  charName: string,
): string {
  return (
    getCategoryForObject(obj, categoryName)?.Attributes.find(
      (c) => c.name === charName,
    )?.value ?? "–"
  );
}

function categoryHasAttributes(categoryName: string): boolean {
  return props.objects.some((obj) => {
    const cat = getCategoryForObject(obj, categoryName);
    return (cat?.Attributes ?? []).some((a) => a.name);
  });
}

function categoryHasVisibleRows(categoryName: string): boolean {
  const allCharNames = new Set<string>();
  for (const obj of props.objects) {
    const cat = getCategoryForObject(obj, categoryName);
    if (cat) {
      for (const child of cat.Attributes) {
        if (child.name) {
          allCharNames.add(child.name);
        }
      }
    }
  }
  if (allCharNames.size === 0) return true;

  const nonSubjectObjects = props.objects.filter(
    (obj) => obj !== subjectObject.value,
  );
  for (const charName of allCharNames) {
    const allNull = props.objects.every((obj) => {
      const cat = getCategoryForObject(obj, categoryName);
      const attr = cat?.Attributes.find((c) => c.name === charName);
      return !attr?.value;
    });
    if (allNull) continue;

    const subjectValue = subjectObject.value
      ? getCharacteristicValue(subjectObject.value, categoryName, charName)
      : "–";
    const allIdentical = nonSubjectObjects.every(
      (obj) =>
        getCharacteristicValue(obj, categoryName, charName) === subjectValue,
    );
    if (!allIdentical) return true;
  }
  return false;
}

const visibleCategoryNames = computed(() => {
  if (!hideIdentical.value) return categoryNames.value;
  return categoryNames.value.filter((name) => categoryHasVisibleRows(name));
});
</script>

<template>
  <div>
    <ComparisonHeader
      v-model:hide-identical="hideIdentical"
      :objects="objects"
      :type-boundary-index="typeBoundaryIndex"
      :column-layout="columnLayout"
      :object-columns-style="objectColumnsStyle"
      @move="onMove"
      @remove="emit('remove', $event)"
    />

    <Accordion v-model:value="expandedPanels" multiple lazy>
      <TuiAccordionPanel
        v-for="categoryName in visibleCategoryNames"
        :key="categoryName"
        :value="categoryName"
        :disabled="!categoryHasAttributes(categoryName)"
        :style="{
          backgroundColor: colors.accordionHeaderBackground,
          minWidth: columnLayout.minWidth,
        }"
        :pt="{ root: { class: '!border-0' } }"
      >
        <TuiAccordionHeader
          :pt="{
            root: { class: '!p-0 !border-0 !bg-transparent' },
            toggleicon: { class: '!hidden' },
          }"
        >
          <CategoryPanel
            :column-layout="columnLayout"
            :type-boundary-index="typeBoundaryIndex"
            :expanded="expandedPanels.includes(categoryName)"
            :expandable="categoryHasAttributes(categoryName)"
            :category-name="categoryName"
            :objects="objects"
            :subject-object="subjectObject"
          />
        </TuiAccordionHeader>
        <TuiAccordionContent v-if="categoryHasAttributes(categoryName)">
          <ComparisonTable
            :column-layout="columnLayout"
            :type-boundary-index="typeBoundaryIndex"
            :category-name="categoryName"
            :objects="objects"
            :subject-object="subjectObject"
            :hide-identical="hideIdentical"
          />
        </TuiAccordionContent>
      </TuiAccordionPanel>
    </Accordion>
  </div>
</template>
